import { createContext, useContext, ReactNode, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { resolveApiUrl } from "@/lib/api-url";
import { queryClient } from "@/lib/queryClient";

type AuthSession = {
  id?: string;
  [key: string]: unknown;
} | null;

type AuthUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  roleLabel?: string | null;
  permissions?: unknown;
  personId?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  [key: string]: unknown;
} | null;

type AuthSessionResponse = {
  session: AuthSession;
  user: AuthUser;
} | null;

interface AuthContextType {
  session: AuthSession;
  user: AuthUser;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery<AuthSessionResponse>({
    queryKey: ["auth-session"],
    queryFn: async () => {
      const response = await fetch(resolveApiUrl("/api/session"), {
        credentials: "include",
      });

      if (response.status === 401) {
        return null;
      }

      if (!response.ok) {
        throw new Error((await response.text()) || response.statusText);
      }

      return (await response.json()) as AuthSessionResponse;
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = data?.user?.id ?? null;
    if (previousUserIdRef.current !== currentUserId) {
      previousUserIdRef.current = currentUserId;
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "auth-session",
      });
    }
  }, [data?.user?.id]);

  useEffect(() => {
    if (!data?.user) {
      Sentry.setUser(null);
      Sentry.setTag("role", "anonymous");
      Sentry.setContext("auth", null);
      return;
    }

    Sentry.setUser({
      id: data.user.id,
      email: data.user.email ?? undefined,
      username: data.user.name ?? undefined,
    });
    Sentry.setTag("role", data.user.role ?? "unknown");
    Sentry.setContext("auth", {
      role: data.user.role ?? null,
      roleLabel: data.user.roleLabel ?? null,
      personId: data.user.personId ?? null,
      organizationId: data.user.organizationId ?? null,
      organizationName: data.user.organizationName ?? null,
    });
  }, [data?.user]);

  const value: AuthContextType = {
    session: data?.session ?? null,
    user: data?.user ?? null,
    isLoading,
    isAuthenticated: !!data?.user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
